import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { actions } from 'react-redux-form';
import { Action, Dispatch } from 'redux';

import { AppState } from '..';
import { PageViewModel } from '@models/common.models';
import { escrowService } from '@services/EscrowService';
import { EscrowOfficeViewModel, OfficeSearchViewModel } from '@models/EscrowOffice';
import { OfficerSearchViewModel } from '@models/EscrowOfficer';
import FormNames from '@constants/FormNames.enum';
import { history } from '../index';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum EscrowOfficeActionType {
  /********** Save **********/
  SaveEscrowOfficeStart = 'SaveEscrowOfficeStart',
  SaveEscrowOfficeSuccess = 'SaveEscrowOfficeSuccess',
  SaveEscrowOfficeFailure = 'SaveEscrowOfficeFailure',

  /********** Get **********/
  GetEscrowOfficeStart = 'GetEscrowOfficeStart',
  GetEscrowOfficeSuccess = 'GetEscrowOfficeSuccess',
  GetEscrowOfficeFailure = 'GetEscrowOfficeFailure',
  GetEscrowOfficeDetailStart = 'GetEscrowOfficeDetailStart',
  GetEscrowOfficeDetailSuccess = 'GetEscrowOfficeDetailSuccess',
  GetEscrowOfficeDetailFailure = 'GetEscrowOfficeDetailFailure',
  GetEscrowOfficersByOfficeStart = 'GetEscrowOfficersByOfficeStart',
  GetEscrowOfficersByOfficeSuccess = 'GetEscrowOfficersByOfficeSuccess',
  GetEscrowOfficersByOfficeFailure = 'GetEscrowOfficersByOfficeFailure',

  /**** Search ******/
  EscrowOfficeSearchInit = 'EscrowOfficeSearchInit',
  EscrowOfficeSearchSuccess = 'EscrowOfficeSearchSuccess',
  EscrowOfficeSearchFailure = 'EscrowOfficeSearchFailure'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/********** Search **********/
export interface EscrowOfficeSearchInitAction extends Action {
  readonly query: string | null;
  readonly type: EscrowOfficeActionType.EscrowOfficeSearchInit;
}
export interface EscrowOfficeSearchSuccessAction extends Action {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<OfficeSearchViewModel>;
  readonly type: EscrowOfficeActionType.EscrowOfficeSearchSuccess;
}
export interface EscrowOfficeSearchFailureAction extends Action {
  readonly errorMessage: string;
  readonly type: EscrowOfficeActionType.EscrowOfficeSearchFailure;
}

/********** Save **********/
export interface SaveEscrowOfficeStartAction extends Action {
  readonly type: EscrowOfficeActionType.SaveEscrowOfficeStart;
}

export interface SaveEscrowOfficeSuccessAction extends Action {
  readonly type: EscrowOfficeActionType.SaveEscrowOfficeSuccess;
  readonly currentId: number;
}

export interface EscrowOfficeFailureAction extends Action {
  readonly type: EscrowOfficeActionType.SaveEscrowOfficeFailure;
  readonly errorMessage: string;
}

/********** Get **********/
export interface GetEscrowOfficeStartAction extends Action {
  readonly type: EscrowOfficeActionType.GetEscrowOfficeStart;
}

export interface GetEscrowOfficeSuccessAction extends Action {
  readonly type: EscrowOfficeActionType.GetEscrowOfficeSuccess;
  readonly currentId: number;
}

export interface GetEscrowOfficeFailureAction extends Action {
  readonly type: EscrowOfficeActionType.GetEscrowOfficeFailure;
  readonly errorMessage: string;
}

export interface GetEscrowOfficeDetailStartAction extends Action {
  readonly type: EscrowOfficeActionType.GetEscrowOfficeDetailStart;
}

export interface GetEscrowOfficeDetailSuccessAction extends Action {
  readonly type: EscrowOfficeActionType.GetEscrowOfficeDetailSuccess;
  readonly currentId: number;
  readonly escrowOfficeDetailVM: EscrowOfficeViewModel;
}

export interface GetEscrowOfficeDetailFailureAction extends Action {
  readonly type: EscrowOfficeActionType.GetEscrowOfficeDetailFailure;
  readonly errorMessage: string;
}

export interface GetEscrowOfficersByOfficeStartAction extends Action {
  readonly type: EscrowOfficeActionType.GetEscrowOfficersByOfficeStart;
}

export interface GetEscrowOfficersByOfficeSuccessAction extends Action {
  readonly type: EscrowOfficeActionType.GetEscrowOfficersByOfficeSuccess;
  readonly escrowOfficers: Array<OfficerSearchViewModel>;
}

export interface GetEscrowOfficersByOfficeFailureAction extends Action {
  readonly type: EscrowOfficeActionType.GetEscrowOfficersByOfficeFailure;
  readonly errorMessage: string;
}

// Actions for each action type declared here.
// They should all return an action object.

/********** Get **********/
export const getEscrowOffice = (id?: number, onSuccess?: Function) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(actions.reset(FormNames.EscrowOfficeForm));
    if (id) {
      dispatch(getEscrowOfficeStart());
      try {
        var escrowOfficeVM = await escrowService.getOffice(id);
        if (onSuccess) {
          dispatch(onSuccess(escrowOfficeVM));
        } else {
          dispatch(getEscrowOfficeSuccess(escrowOfficeVM.id));
          dispatch(actions.change(FormNames.EscrowOfficeForm, escrowOfficeVM));
        }
      } catch (e) {
        dispatch(getEscrowOfficeFailure(e.toString()));
      }
    }
  };
};
export function getEscrowOfficeStart(): GetEscrowOfficeStartAction {
  return {
    type: EscrowOfficeActionType.GetEscrowOfficeStart
  };
}
export function getEscrowOfficeSuccess(id: number): GetEscrowOfficeSuccessAction {
  return {
    type: EscrowOfficeActionType.GetEscrowOfficeSuccess,
    currentId: id
  };
}

export function getEscrowOfficeFailure(errorMessage: string): GetEscrowOfficeFailureAction {
  return {
    type: EscrowOfficeActionType.GetEscrowOfficeFailure,
    errorMessage: errorMessage
  };
}

export const getEscrowOfficeDetail = (id: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getEscrowOfficeDetailStart());
    try {
      var escrowOfficeVM = await escrowService.getOffice(id);
      dispatch(getEscrowOfficeDetailSuccess(escrowOfficeVM));
    } catch (e) {
      dispatch(getEscrowOfficeFailure(e.toString()));
    }
  };
};
export function getEscrowOfficeDetailStart(): GetEscrowOfficeDetailStartAction {
  return {
    type: EscrowOfficeActionType.GetEscrowOfficeDetailStart
  };
}
export function getEscrowOfficeDetailSuccess(
  escrowOfficeDetailVM: EscrowOfficeViewModel
): GetEscrowOfficeDetailSuccessAction {
  return {
    type: EscrowOfficeActionType.GetEscrowOfficeDetailSuccess,
    currentId: escrowOfficeDetailVM.id,
    escrowOfficeDetailVM: escrowOfficeDetailVM
  };
}

export function getEscrowOfficeDetailFailure(errorMessage: string): GetEscrowOfficeDetailFailureAction {
  return {
    type: EscrowOfficeActionType.GetEscrowOfficeDetailFailure,
    errorMessage: errorMessage
  };
}

export const getEscrowOfficersByOfficeId = (id: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getEscrowOfficersByOfficeStart());
    try {
      var escrowOfficers = await escrowService.getOfficersForOfficeDetail(id);
      dispatch(getEscrowOfficersByOfficeSuccess(escrowOfficers));
    } catch (e) {
      dispatch(getEscrowOfficersByOfficeFailure(e.toString()));
    }
  };
};
export function getEscrowOfficersByOfficeStart(): GetEscrowOfficeDetailStartAction {
  return {
    type: EscrowOfficeActionType.GetEscrowOfficeDetailStart
  };
}
export function getEscrowOfficersByOfficeSuccess(
  escrowOfficers: Array<OfficerSearchViewModel>
): GetEscrowOfficersByOfficeSuccessAction {
  return {
    type: EscrowOfficeActionType.GetEscrowOfficersByOfficeSuccess,
    escrowOfficers: escrowOfficers
  };
}

export function getEscrowOfficersByOfficeFailure(errorMessage: string): GetEscrowOfficersByOfficeFailureAction {
  return {
    type: EscrowOfficeActionType.GetEscrowOfficersByOfficeFailure,
    errorMessage: errorMessage
  };
}

/********** Save **********/
export const saveEscrowOffice = (model: EscrowOfficeViewModel) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(saveEscrowOfficeStart());
    try {
      const id = await escrowService.saveOffice(model);
      dispatch(actions.reset(FormNames.EscrowOfficeForm));
      dispatch(saveEscrowOfficeSuccess(id));
    } catch (e) {
      dispatch(saveEscrowOfficeFailure(e.toString()));
    }
  };
};

export function saveEscrowOfficeStart(): SaveEscrowOfficeStartAction {
  return {
    type: EscrowOfficeActionType.SaveEscrowOfficeStart
  };
}
export function saveEscrowOfficeSuccess(id: number): SaveEscrowOfficeSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Escrow Office Saved Successfully'} />,
    ToastOptions.Success
  );
  history.push(getRoute(RouteDefinitions.escrowoffice.Details, { id: id }));

  return {
    type: EscrowOfficeActionType.SaveEscrowOfficeSuccess,
    currentId: id
  };
}
export function saveEscrowOfficeFailure(errorMessage: string): EscrowOfficeFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to save escrow offices. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: EscrowOfficeActionType.SaveEscrowOfficeFailure,
    errorMessage: errorMessage
  };
}

/********** Search **********/
export const escrowOfficeSearch = (
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string
) => {
  return async (dispatch: Dispatch<OfficeSearchViewModel>) => {
    dispatch(escrowOfficeSearchInit(query));
    try {
      let result: PageViewModel<OfficeSearchViewModel> = await escrowService.searchOfficesFull(
        query,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection
      );
      dispatch(escrowOfficeSearchSuccess(pageNumber, pageSize, orderBy, result));
    } catch (e) {
      dispatch(escrowOfficeSearchFailure(e.toString()));
    }
  };
};
export function escrowOfficeSearchInit(query: string | null): EscrowOfficeSearchInitAction {
  return {
    query: query,
    type: EscrowOfficeActionType.EscrowOfficeSearchInit
  };
}
export function escrowOfficeSearchFailure(errorMessage: string): EscrowOfficeSearchFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to search escrow offices. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: EscrowOfficeActionType.EscrowOfficeSearchFailure
  };
}
export function escrowOfficeSearchSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<OfficeSearchViewModel>
): EscrowOfficeSearchSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result,
    type: EscrowOfficeActionType.EscrowOfficeSearchSuccess
  };
}



// WEBPACK FOOTER //
// ./src/escrow-office/escrow-office.actions.tsx